import { getFirebaseFunctions } from '../firebase/index';
import { getFirebaseConfig } from '../firebase/config';

interface PendingPageResponse {
  id: string;
  name: string;
  alreadyConnected: boolean;
  instagramAccount: { id: string; username: string } | null;
}

interface PendingPagesResult {
  pages: PendingPageResponse[];
  redirect: string | null;
}

const createFunctionsBaseUrl = () => {
  const functionsDomain = import.meta.env.VITE_FIREBASE_FUNCTIONS_DOMAIN;
  if (functionsDomain) {
    return functionsDomain.endsWith('/') ? functionsDomain.slice(0, -1) : functionsDomain;
  }
  const region = import.meta.env.VITE_FIREBASE_REGION ?? 'us-central1';
  const { projectId } = getFirebaseConfig();
  return `https://${region}-${projectId}.cloudfunctions.net`;
};

export const startFacebookConnect = (workspaceId: string, initiatorUid: string, redirectPath?: string) => {
  const baseUrl = createFunctionsBaseUrl();
  const redirectUrl = new URL(`${window.location.origin}/integrations/facebook/select`);
  redirectUrl.searchParams.set('workspaceId', workspaceId);
  if (redirectPath) {
    redirectUrl.searchParams.set('returnTo', redirectPath);
  }

  const params = new URLSearchParams({
    workspaceId,
    initiatorUid,
    redirect: redirectUrl.toString(),
  });

  const url = `${baseUrl}/facebookConnect?${params.toString()}`;
  window.location.href = url;
};

const getCallable = async <TArgs extends object, TResult = unknown>(name: string) => {
  const functions = await getFirebaseFunctions();
  const { httpsCallable } = await import('firebase/functions');
  return httpsCallable<TArgs, TResult>(functions, name);
};

export const fetchPendingFacebookPages = async (
  workspaceId: string,
  stateId: string
): Promise<PendingPagesResult> => {
  const callable = await getCallable<{ workspaceId: string; stateId: string }, PendingPagesResult>('facebookGetPendingPages');
  const response = await callable({ workspaceId, stateId });
  return response.data;
};

export const confirmFacebookPages = async (
  workspaceId: string,
  stateId: string,
  pageIds: string[]
) => {
  const callable = await getCallable<
    { workspaceId: string; stateId: string; selectedPageIds: string[] },
    { success: boolean }
  >('facebookConfirmPages');
  await callable({ workspaceId, stateId, selectedPageIds: pageIds });
};

export const cancelFacebookConnection = async (workspaceId: string, stateId: string) => {
  const callable = await getCallable<{ workspaceId: string; stateId: string }, { success: boolean }>('facebookCancelPending');
  await callable({ workspaceId, stateId });
};

export type { PendingPageResponse as PendingFacebookPage, PendingPagesResult };
